<script setup lang="ts">
import { fromTimeString, toTimeString } from '@/libs/utilities/gql-scalar'
import {
  addMinutes,
  differenceInMinutes,
  format,
  startOfMinute,
} from 'date-fns'
import {
  useUpdateScheduledTimeOfChatMessageSendingJobMutation,
  type ChatMessageSendingJobDetailPageScheduledTimeFragment,
} from './ChatMessageSendingJobDetailPageScheduledTime.generated'
import { computed, ref } from 'vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import DolphinDateInput from '@/components/atoms/DolphinDateInput.vue'
import { useAlert } from '@/libs/alertPortal'
import { ChatMessageSendingJobStatus } from '@/types/schema.generated'

const props = defineProps<{
  fragment: ChatMessageSendingJobDetailPageScheduledTimeFragment
  refetch: () => Promise<void>
}>()

const updateMutation = useUpdateScheduledTimeOfChatMessageSendingJobMutation()

const alert = useAlert()

const now = startOfMinute(new Date())

const isEditable = computed(
  () =>
    props.fragment.chatMessageSendingJob?.status ===
    ChatMessageSendingJobStatus.ChatMessageSendingJobStatusPending
)
const isEditing = ref(false)
const editingBuffer = ref<Date | null>(
  props.fragment.chatMessageSendingJob?.scheduledAt
    ? fromTimeString(props.fragment.chatMessageSendingJob?.scheduledAt)
    : null
)

const startEditing = () => {
  isEditing.value = true
  editingBuffer.value = props.fragment.chatMessageSendingJob?.scheduledAt
    ? fromTimeString(props.fragment.chatMessageSendingJob?.scheduledAt)
    : null
}

const finishEditing = async () => {
  const scheduledAt = editingBuffer.value
  if (!scheduledAt) {
    alert.error('日時を選択してください')
    return
  }

  const guid = props.fragment.chatMessageSendingJob?.guid
  if (!guid) {
    // ここは起きるはずがないエラー(propsのfragmentがなければ表示されないはずなので)
    alert.error('エラーが発生しました')
    return
  }

  if (
    scheduledAt <= addMinutes(Date.now(), 5) // 5分以内に設定されているとき
  ) {
    const minutes = differenceInMinutes(scheduledAt, Date.now(), {
      roundingMethod: 'ceil',
    })
    if (
      !window.confirm(
        `メッセージは${minutes}分以内に配信されます。よろしいですか？`
      )
    ) {
      return
    }
  }

  await updateMutation.mutate({
    input: {
      guid,
      scheduledAt: toTimeString(scheduledAt),
    },
  })

  await props.refetch()

  isEditing.value = false
}

const formatNullableTimeString = (
  time: TimeString | null | undefined
): string => {
  if (!time) {
    return '-'
  }

  return format(fromTimeString(time), 'yyyy/MM/dd HH:mm')
}
</script>
<template>
  <div :class="$style.editingContent" v-if="isEditing && isEditable">
    <DolphinDateInput v-model="editingBuffer" :min-date="now" />
    <FlatButton size="small" @click="finishEditing">保存</FlatButton>
  </div>
  <div :class="$style.content" v-else>
    <div>
      {{
        formatNullableTimeString(
          props.fragment.chatMessageSendingJob?.scheduledAt
        )
      }}
    </div>
    <FlatButton
      v-if="isEditable"
      variant="base"
      size="small"
      @click="startEditing"
    >
      編集
    </FlatButton>
  </div>
</template>
<style module lang="scss">
.editingContent {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}
</style>
